import React, { useRef } from "react";
import styled from "styled-components";
import useCompanies from "../hooks/useCompanies";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useTranslation } from "react-i18next";
import { OverlayPanel } from "primereact/overlaypanel";
import useCompany from "../hooks/useCompany";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 182px;
  margin: 15px auto;
`;

const OverlayPanelInput = styled.div`
  display: flex;
  align-items: center;
  height: 48px;
  width: 100%;
  background-color: #fdfdfd;
  border-radius: 4px;
  justify-content: space-between;
  padding: 0 1rem;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  &:focus {
    outline: none;
    border: 1px solid #007ad9;
  }
`;

const PanelTree = styled.div`
  display: flex;
  margin-top: -2px;
  flex-direction: column;
  background-color: #fdfdfd;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 5px 5px;
  width: 182px;
  border: 1px solid #007ad9;
`;

const TreeItem = styled.div`
  font-size: 14px;
  color: var(--text-color);
  padding: 0.5rem 1rem;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
    cursor: pointer;
  }
`;

interface OrganizationSelectProps {
  disabled?: boolean;
}

const OrganizationSelect: React.FC<OrganizationSelectProps> = ({
  disabled = false,
}) => {
  const { t } = useTranslation();
  const op = useRef<OverlayPanel>(null);
  const [
    companyId,
    setCompanyId,
    organizationId,
    setOrganizationId,
    organizationName,
    setOrganizationName,
    companyName,
    setCompanyName,
  ] = useUIStore(
    useShallow((state: UIState) => [
      state.companyId,
      state.setCompanyId,
      state.organizationId,
      state.setOrganizationId,
      state.organizationName,
      state.setOrganizationName,
      state.companyName,
      state.setCompanyName,
    ])
  );
  const { companies, isLoadingCompanies, isCompaniesSuccess } = useCompanies();

  const { company, isLoadingCompany } = useCompany({
    companyId: companyId,
    enabled: !!companyId && isCompaniesSuccess,
  });

  const selectedOrganization = company?.organizations.find(
    (org) => org.id === organizationId
  );

  const loading = isLoadingCompanies || isLoadingCompany;

  return (
    <Container>
      <OverlayPanelInput
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter") {
            op.current?.toggle(e);
            setTimeout(() => {
              const el = document.getElementById("tree-item-0");
              el?.focus();
            }, 0);
          }
        }}
        onClick={disabled ? undefined : (e) => op.current?.toggle(e)}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {loading ? (
          <i className="pi pi-spin pi-spinner" />
        ) : (
          <>
            <div>
              {!company && !selectedOrganization && (
                <div>{t("label-select-organization")}</div>
              )}
              {company && !selectedOrganization && (
                <div>{company?.companyName}</div>
              )}
              {company && selectedOrganization && (
                <div>{selectedOrganization?.organizationName}</div>
              )}
            </div>

            <i
              className="pi pi-chevron-down"
              onClick={disabled ? undefined : (e) => op.current?.toggle(e)}
              style={{
                fontSize: "0.6rem",
                color: disabled ? "#ccc" : "#000",
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            />
          </>
        )}
      </OverlayPanelInput>
      <OverlayPanel ref={op} unstyled>
        <PanelTree>
          {companies?.map((company, index) => {
            if (!company) {
              return null;
            }

            return (
              <div key={company.id}>
                <TreeItem
                  role="button"
                  tabIndex={0}
                  id={`tree-item-${index}`}
                  className={`tree-item-${index}`}
                  onClick={() => {
                    setCompanyId(company.id);
                    setCompanyName(company.companyName);
                    setOrganizationId(null);
                    setOrganizationName(null);
                    op.current?.hide();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setCompanyId(company.id);
                      setCompanyName(company.companyName);
                      setOrganizationId(null);
                      setOrganizationName(null);
                      op.current?.hide();
                    }
                  }}
                >
                  {company.companyName}
                </TreeItem>
                <div>
                  {company.organizations.map((organization, orgIndex) => (
                    <TreeItem
                      role="button"
                      tabIndex={0}
                      id={`tree-item-${index}-${orgIndex}`}
                      key={organization.id}
                      style={{
                        padding: "0.5rem 2rem",
                      }}
                      onClick={() => {
                        setCompanyId(company.id);
                        setCompanyName(company.companyName);
                        setOrganizationId(organization.id as number);
                        setOrganizationName(organization.organizationName);
                        op.current?.hide();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setCompanyId(company.id);
                          setCompanyName(company.companyName);
                          setOrganizationId(organization.id as number);
                          setOrganizationName(organization.organizationName);
                          op.current?.hide();
                        }
                      }}
                    >
                      {organization.organizationName}
                    </TreeItem>
                  ))}
                </div>
              </div>
            );
          })}
        </PanelTree>
      </OverlayPanel>
    </Container>
  );
};

export default OrganizationSelect;
